import { FileType } from '../types';

export const downloadBase64File = (
    contentType: string,
    base64Content: string,
    fileName: string
): void => {
    const href = `data:${contentType};base64,${base64Content}`;
    const element = document.createElement('a');
    element.href = href;
    element.download = fileName;
    element.click();
};

export const getFileExtension = (fileType: FileType): string => {
    switch (fileType) {
        case FileType.Pdf:
            return 'pdf';
        case FileType.Csv:
            return 'csv';
        default:
            throw new Error(`Unsupported file type: ${fileType}`);
    }
};

const MIN_BYTES_FOR_MIME_DETECTION = 4;
const BYTES_TO_READ_FOR_MIME_DETECTION = 12;
const DEFAULT_IMAGE_MIME_TYPE = 'image/jpeg';

const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47];
const GIF_SIGNATURE = [0x47, 0x49, 0x46, 0x38];
const WEBP_SIGNATURE = [0x57, 0x45, 0x42, 0x50];
const WEBP_SIGNATURE_OFFSET = 8;

const URL_PREFIXES = ['data:', 'http://', 'https://'] as const;

const isBase64 = (str: string): boolean => {
    if (!str || str.length === 0) {
        return false;
    }

    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(str)) {
        return false;
    }

    try {
        return btoa(atob(str)) === str;
    } catch {
        return false;
    }
};

const matchesFileSignature = (bytes: Uint8Array, signature: number[], offset = 0): boolean => {
    if (bytes.length < offset + signature.length) {
        return false;
    }

    return signature.every((byte, index) => bytes[offset + index] === byte);
};

const getImageMimeType = (binaryString: string): string => {
    if (binaryString.length < MIN_BYTES_FOR_MIME_DETECTION) {
        return DEFAULT_IMAGE_MIME_TYPE;
    }

    const bytesToRead = Math.min(binaryString.length, BYTES_TO_READ_FOR_MIME_DETECTION);
    const bytes = new Uint8Array(bytesToRead);
    for (let i = 0; i < bytesToRead; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    if (matchesFileSignature(bytes, JPEG_SIGNATURE)) {
        return 'image/jpeg';
    }
    if (matchesFileSignature(bytes, PNG_SIGNATURE)) {
        return 'image/png';
    }
    if (matchesFileSignature(bytes, GIF_SIGNATURE)) {
        return 'image/gif';
    }
    if (matchesFileSignature(bytes, WEBP_SIGNATURE, WEBP_SIGNATURE_OFFSET)) {
        return 'image/webp';
    }

    return DEFAULT_IMAGE_MIME_TYPE;
};

const isAlreadyUrl = (content: string): boolean => {
    return URL_PREFIXES.some((prefix) => content.startsWith(prefix));
};

export const buildBase64ImageUrl = (contentBase64: string | undefined): string | null => {
    if (!contentBase64) {
        return null;
    }

    if (isAlreadyUrl(contentBase64)) {
        return contentBase64;
    }

    try {
        const base64String = isBase64(contentBase64) ? contentBase64 : btoa(contentBase64);

        let sampleForMimeType: string;
        if (isBase64(contentBase64)) {
            const decodedBinary = atob(contentBase64);
            sampleForMimeType = decodedBinary.substring(0, BYTES_TO_READ_FOR_MIME_DETECTION);
        } else {
            sampleForMimeType = contentBase64.substring(0, BYTES_TO_READ_FOR_MIME_DETECTION);
        }

        const mimeType = getImageMimeType(sampleForMimeType);

        return `data:${mimeType};base64,${base64String}`;
    } catch {
        return null;
    }
};

export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64String = (reader.result as string).split(',')[1];
            resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

export const openFileInBrowserTab = (file: File): void => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
    URL.revokeObjectURL(url);
};
